import { useState, type FormEvent } from "react";
import { AuthLayout } from "./auth-layout";
import { api, ApiError } from "@/api/client";
import { useSession } from "@/api/session";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { LabelledInput } from "@/components/ui/field";

/**
 * Forced first-login reset. The backend hands back a FRESH token here: the one
 * already in hand still carries mustResetPassword in its signed payload, so
 * adopting the new token is what actually unlocks the rest of the app.
 */
export function ResetPasswordPage() {
  const adoptToken = useSession((s) => s.adoptToken);
  const logout = useSession((s) => s.logout);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldError(null);

    if (password.length < 8) {
      setFieldError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setFieldError("The two passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const result = await api<{ ok: true; token: string }>("/api/auth/reset-password", {
        method: "POST",
        body: { newPassword: password },
      });
      adoptToken(result.token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update your password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Set your password"
      sub="You signed in with a one-time password. Choose your own before continuing."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {error && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-lg bg-bad-wash px-3 py-2.5 text-[13px] leading-relaxed text-bad"
          >
            <Icon name="warning-circle" size={16} className="mt-0.5" />
            <span>{error}</span>
          </p>
        )}

        <LabelledInput
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          hint="At least 8 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <LabelledInput
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          error={fieldError}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-1 w-full">
          Save and continue
        </Button>

        <Button type="button" variant="ghost" size="sm" onClick={logout} className="self-center">
          Sign in as someone else
        </Button>
      </form>
    </AuthLayout>
  );
}
